import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { trackingApi, type TrackPoint } from '@/api/client';
import { formatDateTime } from '@/lib/format';

/**
 * "Route so far" for a trip, drawn from the crew app's GPS points. Renders nothing when the
 * trip has no recorded locations (or the viewer can't see them).
 */
export function TripRouteMap({ tripId, live }: { tripId: string; live?: boolean }) {
  const [points, setPoints] = useState<TrackPoint[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      trackingApi
        .tripTrack(tripId)
        .then((res) => !cancelled && setPoints(res.points))
        .catch(() => !cancelled && setPoints((prev) => prev ?? []));
    void load();
    // While the trip is moving, keep the line current.
    const timer = live ? window.setInterval(() => void load(), 60_000) : undefined;
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [tripId, live]);

  const hasPoints = Boolean(points && points.length);

  useEffect(() => {
    if (!hasPoints || !containerRef.current) return;
    if (!mapRef.current) {
      const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      layerRef.current = L.layerGroup().addTo(map);
      setTimeout(() => map.invalidateSize(), 50);
    }
    const map = mapRef.current;
    const layer = layerRef.current!;
    layer.clearLayers();
    const latlngs = points!.map((p) => [p.latitude, p.longitude] as [number, number]);
    L.polyline(latlngs, { color: '#0E1216', weight: 4, opacity: 0.8 }).addTo(layer);
    L.circleMarker(latlngs[0]!, { radius: 6, color: '#0E1216', fillColor: '#FFFFFF', fillOpacity: 1, weight: 2 })
      .bindTooltip(`Start · ${formatDateTime(points![0]!.recordedAt)}`)
      .addTo(layer);
    const last = points![points!.length - 1]!;
    L.circleMarker(latlngs[latlngs.length - 1]!, { radius: 7, color: '#0E1216', fillColor: '#FFD100', fillOpacity: 1, weight: 3 })
      .bindTooltip(`Latest · ${formatDateTime(last.recordedAt)}`)
      .addTo(layer);
    map.fitBounds(L.latLngBounds(latlngs).pad(0.15), { maxZoom: 15 });
  }, [hasPoints, points]);

  useEffect(
    () => () => {
      mapRef.current?.remove();
      mapRef.current = null;
    },
    [],
  );

  if (!hasPoints) return null;
  const last = points![points!.length - 1]!;

  return (
    <section className="detail-section">
      <h3>Route so far</h3>
      <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 0 }}>
        From the crew app · last location {formatDateTime(last.recordedAt)}
      </p>
      <div ref={containerRef} className="trip-route-map" />
      <style>{`
        .trip-route-map {
          height: 280px;
          width: 100%;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          z-index: 0;
        }
      `}</style>
    </section>
  );
}
